// React & Hooks
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers
import { useTranslate } from "helpers";

// Types & Constants
import {
  VehicleFireExtinguisherSearchPageCmp,
  VehicleFireExtinguisherSearchPageProps,
} from "./types";
import {
  FleetVehicleFireExtinguisherRow,
  FleetVehicleFireExtinguisherSearchFormValues,
} from "services/api/fleet/vehicles/fireextinguishers/types";
import { FleetVehicleDetailFormValues } from "services/api/fleet/vehicles/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import {
  FLEET_VEHICLE_FIRE_EXTINGUISHER_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// API
import { deleteFleetVehicleFireExtinguisherAPI, getFleetVehicleFireExtinguishersAPI } from "services/api/fleet/vehicles/fireextinguishers/api";

// Search & Pagination
import { Pagination } from "services/search";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// UI Components
import { Button } from "ui/components/Button/Button";
import { NoResults } from "ui/components/NoResults";

// Navigation
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// Local Components
import VehicleFireExtinguisherSearchFilters from "./components/FireExtinguisherSearchFilters/FireExtinguisherSearchFilters";
import VehicleFireExtinguisherSearchResults from "./components/FireExtinguisherSearchResults/FireExtinguisherSearchResults";
import TabMenu from "ui/modules/fleet/vehicles/pages/components/TabMenu/TabMenu";
import { VehicleTabHeader } from "ui/modules/fleet/vehicles/pages/components/VehicleTabHeader";
import VehicleFireExtinguisherEditCard from "../VehicleFireExtinguisherEditCard/VehicleFireExtinguisherEditCard";
import { Dialog } from "ui/components/Dialog";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";
import { Errors } from "services/forms/validation/types";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";

const VehicleFireExtinguisherSearchPage: VehicleFireExtinguisherSearchPageCmp = (
  props: VehicleFireExtinguisherSearchPageProps
) => {
  const { id } = useParams();

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetVehicleDetailFormValues>(
    initialFleetVehicleDetailForm
  );
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleFireExtinguisherRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [fireExtinguisherId, setFireExtinguisherId] = useState<string>("");
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (id) {
      fetchVehicle(id);
      fetchSearchResult(id, searchResults.pagination);
    }
  }, [id]);

  const fetchVehicle = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetVehicleAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const fetchSearchResult = async (id: string, pagination: Pagination, name?: string | null) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleFireExtinguishersAPI(id, pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setFireExtinguisherId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setFireExtinguisherId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    if (id) fetchSearchResult(id, searchResults.pagination);

    setFireExtinguisherId("");
    setShowAddNewModal(false);
  }, []);

  const handleSearchChange = useCallback((formValues: FleetVehicleFireExtinguisherSearchFormValues) => {
    if (id) fetchSearchResult(id, searchResults.pagination, formValues.search);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id) fetchSearchResult(id, newPagination);
  };


  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setFireExtinguisherId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!fireExtinguisherId) return;

    try {
      const deleted = await deleteFleetVehicleFireExtinguisherAPI(fireExtinguisherId, t);

      if (deleted && id) {
        await fetchSearchResult(id, searchResults.pagination);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setFireExtinguisherId("");
    } catch (err) {
      console.error("Greška prilikom brisanja troška:", err);
    }
  }, [fireExtinguisherId, t]);

  const handleDeleteDialogClose = useCallback(() => {
    setFireExtinguisherId("");
    setOpenDialog(false);
  }, []);

  // Promjena statusa preko značke — vozilo se dohvaća ponovno da se značka osvježi.
  const handleStatusChange = useCallback(() => {
    if (id) fetchVehicle(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <VehicleTabHeader
            vehicleId={id}
            vehicle={formValues}
            title={t(FLEET_VEHICLE_FIRE_EXTINGUISHER_SEARCH_PAGE_TITLE.text)}
            icon={FLEET_VEHICLE_FIRE_EXTINGUISHER_SEARCH_PAGE_TITLE.icon}
            iconColor={FLEET_VEHICLE_FIRE_EXTINGUISHER_SEARCH_PAGE_TITLE.iconColor}
            onStatusChange={handleStatusChange}
          >
            <Button
              variant="primary"
              children={t("admin.vehicle.fireextinguisher.search.add")}
              startIcon="/media/icons/plus.svg"
              onClick={handleAddNew}
            />
          </VehicleTabHeader>
        </div>
        <TabMenu vehicleId={id} />
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <VehicleFireExtinguisherSearchFilters onSearchChange={handleSearchChange} />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.fleet.vehicle.fireextinguisher.search.noresults")} />
          ) : (
            <VehicleFireExtinguisherSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>
      <VehicleFireExtinguisherEditCard
        show={showAddNewModal}
        vehicleId={id}
        fireExtinguisherId={fireExtinguisherId}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.fireextinguisher.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.fireextinguisher.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.fireextinguisher.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.fireextinguisher.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

VehicleFireExtinguisherSearchPage.route =
  FleetVehicleRoutes.FleetVehicleFireExtinguisherSearchPageRouter;

export default VehicleFireExtinguisherSearchPage;
